/** sessionStorage key used to prevent reload loops after a stale-chunk recovery. */
export const STALE_BUILD_RELOAD_STORAGE_KEY = 'tp-stale-build-reload-at';

/** Ignore a second reload attempt within this window (ms). */
export const STALE_BUILD_RELOAD_GUARD_MS = 10_000;

const CHUNK_LOAD_MESSAGE = /Loading (CSS )?chunk .+ failed/i;

/**
 * True when the error looks like a failed webpack/Nx lazy chunk load
 * (typical after a deploy replaced hashed assets the current tab still references).
 */
export function isChunkLoadError(error: unknown): boolean {
    if (error == null) return false;

    if (typeof error === 'string') {
        return CHUNK_LOAD_MESSAGE.test(error);
    }

    if (typeof error !== 'object') return false;

    const { name, message } = error as { name?: unknown; message?: unknown };
    if (name === 'ChunkLoadError') return true;
    if (typeof message === 'string' && CHUNK_LOAD_MESSAGE.test(message)) return true;

    return false;
}

/** Browser reload — untestable under jsdom (Location.reload is not stubbable). */
/* istanbul ignore next */
function defaultReloadPage(): void {
    window.location.reload();
}

/**
 * Reloads the page once to pick up a newly deployed build.
 * Returns true if a reload was triggered; false if skipped by the guard
 * (so callers can fall through to a normal error UI).
 * Optional `reloadPage` is for unit tests only.
 */
export function reloadForStaleBuild(reloadPage: () => void = defaultReloadPage): boolean {
    if (typeof window === 'undefined') return false;

    try {
        const raw = sessionStorage.getItem(STALE_BUILD_RELOAD_STORAGE_KEY);
        const lastReloadAt = raw ? Number(raw) : NaN;
        if (Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < STALE_BUILD_RELOAD_GUARD_MS) {
            return false;
        }
        sessionStorage.setItem(STALE_BUILD_RELOAD_STORAGE_KEY, String(Date.now()));
    } catch {
        // sessionStorage unavailable — still attempt a single reload
    }

    reloadPage();
    return true;
}

type WindowListeners = {
    onRejection: (event: PromiseRejectionEvent) => void;
    onError: (event: ErrorEvent) => void;
};

let installedListeners: WindowListeners | null = null;

/**
 * Installs global listeners so dynamic-import failures outside React still
 * trigger a one-shot reload. Returns an uninstall function (for tests).
 * Optional `reloadPage` is for unit tests only.
 */
export function installStaleBuildRecovery(reloadPage: () => void = defaultReloadPage): () => void {
    if (typeof window === 'undefined' || installedListeners) {
        return () => undefined;
    }

    const onRejection = (event: PromiseRejectionEvent) => {
        if (isChunkLoadError(event.reason) && reloadForStaleBuild(reloadPage)) {
            event.preventDefault();
        }
    };

    const onError = (event: ErrorEvent) => {
        if ((isChunkLoadError(event.error) || isChunkLoadError(event.message)) && reloadForStaleBuild(reloadPage)) {
            event.preventDefault();
        }
    };

    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError);
    installedListeners = { onRejection, onError };

    return () => {
        if (!installedListeners) return;
        window.removeEventListener('unhandledrejection', installedListeners.onRejection);
        window.removeEventListener('error', installedListeners.onError);
        installedListeners = null;
    };
}
